import React, { useMemo } from 'react';
import { Table, Tag, Typography, Tooltip } from '@arco-design/web-react';
import { useTranslation } from 'react-i18next';
import {
  createStringSorter,
  createNumberSorter,
  createDateSorter,
  ResizableTitle,
  useResizableColumns,
} from '@/modules/common/helpers/TableHelpers';
import { formatShortDate } from '@/modules/common/helpers/dateHelpers';

const { Paragraph } = Typography;

const SERVICE_KEYS = [
  { key: 'chat', color: 'blue' },
  { key: 'generation', color: 'green' },
  { key: 'temario', color: 'purple' },
  { key: 'cil', color: 'orangered' },
  { key: 'quiz', color: 'magenta' },
  { key: 'summary', color: 'cyan' },
  { key: 'agent', color: 'gold' },
  { key: 'script', color: 'gray' },
  { key: 'selective_regenerator', color: 'arcoblue' },
];

// Map from key to i18n key suffix (selective_regenerator -> selectiveRegenerator)
const SERVICE_I18N_MAP = {
  chat: 'chat',
  generation: 'generation',
  temario: 'temario',
  cil: 'cil',
  quiz: 'quiz',
  summary: 'summary',
  agent: 'agent',
  script: 'script',
  selective_regenerator: 'selectiveRegenerator',
};

const formatDuration = (seconds) => {
  if (seconds == null || seconds <= 0) return '—';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins < 60) return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remainMins = mins % 60;
  return remainMins > 0 ? `${hrs}h ${remainMins}m` : `${hrs}h`;
};

const columnConstraints = {
  date: { minWidth: 75, maxWidth: 110 },
  caller: { minWidth: 85, maxWidth: 120 },
  description: { minWidth: 130, maxWidth: 280 },
  user_name: { minWidth: 100, maxWidth: 190 },
  calls: { minWidth: 50, maxWidth: 70 },
  duration: { minWidth: 60, maxWidth: 85 },
  input_tokens: { minWidth: 65, maxWidth: 95 },
  output_tokens: { minWidth: 65, maxWidth: 95 },
  cost_usd: { minWidth: 65, maxWidth: 95 },
};

const LlmCostTable = ({ items, total, page, pageSize, onPageChange, loading, activeCallers }) => {
  const { t } = useTranslation('finance');

  const activeSet = useMemo(() => new Set(activeCallers || []), [activeCallers]);

  // Build service map with translated labels
  const serviceMap = useMemo(() => {
    const map = {};
    SERVICE_KEYS.forEach((svc) => {
      const i18nKey = SERVICE_I18N_MAP[svc.key];
      map[svc.key] = {
        key: svc.key,
        label: t(`costs.services.${i18nKey}.label`),
        desc: t(`costs.services.${i18nKey}.desc`),
        color: svc.color,
      };
    });
    return map;
  }, [t]);

  // Build filter options: active services colored, inactive grayed
  const callerFilters = useMemo(() =>
    SERVICE_KEYS.map((svc) => {
      const hasData = activeSet.has(svc.key);
      const info = serviceMap[svc.key];
      return {
        text: (
          <span style={{ opacity: hasData ? 1 : 0.4 }}>
            <Tag color={hasData ? svc.color : 'gray'} size="small" style={{ marginRight: 6 }}>
              {info.label}
            </Tag>
            <span style={{ fontSize: 12, color: '#86909c' }}>{info.desc}</span>
          </span>
        ),
        value: svc.key,
      };
    }),
  [activeSet, serviceMap]);

  const baseColumns = useMemo(() => [
    {
      title: t('common.columns.date'),
      dataIndex: 'date',
      key: 'date',
      width: 80,
      sorter: createDateSorter('date'),
      render: (val) => formatShortDate(val),
    },
    {
      title: t('common.columns.service'),
      dataIndex: 'caller',
      key: 'caller',
      width: 95,
      align: 'center',
      filters: callerFilters,
      onFilter: (value, record) => record.caller === value,
      filterMultiple: false,
      render: (caller) => {
        const svc = serviceMap[caller];
        return (
          <Tooltip content={svc?.desc || caller}>
            <Tag color={svc?.color || 'gray'} size="small">
              {svc?.label || caller}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: t('common.columns.description'),
      dataIndex: 'description',
      key: 'description',
      width: 170,
      sorter: createStringSorter('description'),
      render: (text) => (
        <Paragraph
          style={{ margin: 0, wordBreak: 'normal', overflowWrap: 'normal', whiteSpace: 'normal' }}
          ellipsis={{ rows: 2 }}
        >
          {text || '—'}
        </Paragraph>
      ),
    },
    {
      title: t('common.columns.user'),
      dataIndex: 'user_name',
      key: 'user_name',
      width: 120,
      sorter: createStringSorter('user_name'),
      render: (text) => text || <span style={{ color: '#c9cdd4' }}>—</span>,
    },
    {
      title: t('common.columns.calls'),
      dataIndex: 'calls',
      key: 'calls',
      width: 55,
      align: 'right',
      sorter: createNumberSorter('calls'),
    },
    {
      title: t('common.columns.duration'),
      dataIndex: 'duration_seconds',
      key: 'duration',
      width: 65,
      align: 'right',
      sorter: createNumberSorter('duration_seconds'),
      render: (val) => formatDuration(val),
    },
    {
      title: t('common.columns.input'),
      dataIndex: 'input_tokens',
      key: 'input_tokens',
      width: 70,
      align: 'right',
      sorter: createNumberSorter('input_tokens'),
      render: (val) => (val || 0).toLocaleString(),
    },
    {
      title: t('common.columns.output'),
      dataIndex: 'output_tokens',
      key: 'output_tokens',
      width: 70,
      align: 'right',
      sorter: createNumberSorter('output_tokens'),
      render: (val) => (val || 0).toLocaleString(),
    },
    {
      title: t('common.columns.cost'),
      dataIndex: 'cost_usd',
      key: 'cost_usd',
      width: 75,
      align: 'right',
      sorter: createNumberSorter('cost_usd'),
      defaultSortOrder: 'descend',
      render: (val) => `$${(val || 0).toFixed(4)}`,
    },
  ], [callerFilters, t, serviceMap]);

  const columns = useResizableColumns(baseColumns, columnConstraints, [baseColumns]);

  const components = {
    header: { th: ResizableTitle },
  };

  return (
    <Table
      className="resizable-table"
      columns={columns}
      data={items || []}
      rowKey={(r) => `${r.caller}-${r.date}-${r.description || ''}-${r.user_name || ''}`}
      components={components}
      loading={loading}
      size="small"
      pagination={{
        current: page,
        pageSize,
        total,
        showTotal: true,
        onChange: onPageChange,
        sizeCanChange: true,
        pageSizeChangeResetCurrent: true,
      }}
    />
  );
};

export default LlmCostTable;
